import { ref, computed, toValue } from "vue";
import { useGetWTFIR, useLazyGetPairAlarm, WTFIRInputType, WTFIRTypeData } from "@src/api";

export type PairData = {
  originData: string;
  secondaryData: string | null;
  originType: WTFIRTypeData;
}

const scanningData = ref<Set<string>>(new Set());
const keys = ref<Map<string, string | null>>(new Map());
const data = ref<Map<string, PairData>>(new Map());

export const useCheckAlarming = () => {
  const { execute: getWTFIR } = useGetWTFIR();
  const { execute: getPairAlarm } = useLazyGetPairAlarm();

  const pairCount = computed(() => {
    let result = 0;
    data.value.forEach(item => {
      if (item.originData && item.secondaryData) {
        result++;
      }
    });

    return result;
  });

  const epcWithoutPairCount = computed(() => {
    let result = 0;

    data.value.forEach(item => {
      if (item.originType === WTFIRTypeData.epc && !item.secondaryData) {
        result++;
      }
    });

    return result;
  });

  const alarmWithoutPairCount = computed(() => {
    let result = 0;

    data.value.forEach(item => {
      if (item.originType === WTFIRTypeData.epcAlarm && !item.secondaryData) {
        result++;
      }
    });

    return result;
  });

  const unknownCount = computed(() => {
    let result = 0;

    data.value.forEach(item => {
      if (item.originType === WTFIRTypeData.unknown) {
        result++; 
      }
    });

    return result;
  });

  const epcClearCount = computed(() => {
    let result = 0;

    data.value.forEach(item => {
      if (item.originType === WTFIRTypeData.epcClear) {
        result++;
      }
    });

    return result;
  });

  const alarmClearCount = computed(() => {
    let result = 0;

    data.value.forEach(item => {
      if (item.originType === WTFIRTypeData.epcAlarmClear) {
        result++;
      }
    });

    return result;
  });

  const getTypeAndTransformedValue = async (value: string, sourceType?: WTFIRTypeData) => {
    let type: WTFIRTypeData;
    let transformedValue: string | null = null;

    if (value.startsWith('303')) {
      type = WTFIRTypeData.epc;
      transformedValue = keys.value.get(value) || '30B' + value.slice(3);
    } else if (value.startsWith('30B')) {
      type = WTFIRTypeData.epcAlarm;
      transformedValue = keys.value.get(value) || '303' + value.slice(3);
    } else {
      let resultWTFIRType = sourceType;

      if (!resultWTFIRType) {
        const resultWTFIR = await getWTFIR({ data: value, input_type: WTFIRInputType.rfid });
        resultWTFIRType = resultWTFIR.data.value.type_data;
      }

      if (resultWTFIRType === WTFIRTypeData.epcClear) {
        type = WTFIRTypeData.epcClear;
      } else if (resultWTFIRType === WTFIRTypeData.epcAlarmClear) {
        type = WTFIRTypeData.epcAlarmClear;
      } else if (resultWTFIRType === WTFIRTypeData.unknown) {
        type = WTFIRTypeData.unknown;
      } else if (resultWTFIRType === WTFIRTypeData.epc) {
        type = WTFIRTypeData.epc;

        const resultPairAlarm = await getPairAlarm({ epc: value });
        transformedValue = resultPairAlarm.data.value.epc_alarm;
      }
    }

    return { type, transformedValue };
  }

  const setKey = (value: string, transformedValue: string | null) => {
    if (!keys.value.has(value)) {
      keys.value.set(value, transformedValue);
    }

    if (transformedValue && !keys.value.has(transformedValue)) {
      keys.value.set(transformedValue, value);
    }
  }

  const getKeyAndPair = (value: string, type: WTFIRTypeData, transformedValue: string | null) => {
    let pair: PairData | null = null;
    let key = value;

    if (data.value.has(value)) {
      pair = toValue(data.value.get(value));
      pair.secondaryData = transformedValue;
    } else if (data.value.has(transformedValue)) {
      key = transformedValue;
      pair = toValue(data.value.get(transformedValue));
      pair.secondaryData = value;
    } else {
      pair = {
        originData: value,
        secondaryData: null,
        originType: type
      }
    }

    return { pair, key };
  }

  const add = async (value: string, sourceType?: WTFIRTypeData) => {
    if (scanningData.value.has(value)) {
      const item = getItem(value);

      return { pair: item, key: value, type: item.originType, exist: true };
    } else {
      scanningData.value.add(value);
    }

    const { type, transformedValue } = await getTypeAndTransformedValue(value, sourceType);
    setKey(value, transformedValue);
    const { pair, key } = getKeyAndPair(value, type, transformedValue);
    
    data.value.set(key, pair);

    return { pair, key, type, exist: false };
  }

  const getItem = (value: string) => {
    const secondaryKey = keys.value.get(value);

    return data.value.get(value) || ( secondaryKey ? data.value.get(secondaryKey) : null);
  }

  const deleteItem = (value: string) => {
    const secondaryKey = keys.value.get(value);

    scanningData.value.delete(value);
    data.value.delete(value);

    if (secondaryKey) {
      scanningData.value.delete(secondaryKey);
      data.value.delete(secondaryKey);
    }
  }

  const clear = () => {
    scanningData.value.clear();
    keys.value.clear();
    data.value.clear();
  }

  return {
    data,
    keys,
    pairCount,
    epcClearCount,
    alarmClearCount,
    epcWithoutPairCount,
    alarmWithoutPairCount,
    unknownCount,
    scanningData,
    add,
    clear,
    deleteItem,
    getItem
  }
};